import { extractNode } from '../graph-row'
import { compareByNameThenId } from '../sort'
import type {
  CypherReadResult,
  PlantEquipment,
  PlantGenericEntity,
  PlantLine,
  PlantPartChild,
  PlantPartDetail,
  PlantPartDetailNode,
  PlantPartLocation,
  PlantPartProcedure,
  PlantPartRef,
  PlantPartUsedIn,
  PlantPartVendor,
  PlantProcedureRef,
  PlantSite,
  PlantVendorRef,
  PlantVendorRole
} from '../types'
import { extractPlantEdges } from './edges'
import { extractFaultList, withDisplayName } from './equipment-relationships'

function optionalNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim()) {
    const number = Number(value)
    return Number.isFinite(number) ? number : undefined
  }
  return undefined
}

function optionalBoolean(value: unknown): boolean | undefined {
  return typeof value === 'boolean' ? value : undefined
}

function labels(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((label): label is string => typeof label === 'string')
    : []
}

function extractUsedIn(value: unknown): PlantPartUsedIn[] {
  if (!Array.isArray(value)) return []
  const byOwner = new Map<string, PlantPartUsedIn>()
  for (const item of value) {
    if (!item || typeof item !== 'object') continue
    const row = item as Record<string, unknown>
    const owner = extractNode<PlantGenericEntity>(row['owner'])
    const edge = extractPlantEdges([row['edge']])[0]
    if (!owner || !edge) continue
    const existing = byOwner.get(owner.id)
    if (!existing || (existing.edge.type !== 'HAS_PART' && edge.type === 'HAS_PART')) {
      byOwner.set(owner.id, { owner, ownerLabels: labels(row['ownerLabels']), edge })
    }
  }
  return [...byOwner.values()].sort((a, b) => compareByNameThenId(a.owner, b.owner))
}

function extractSubParts(value: unknown): PlantPartChild[] {
  if (!Array.isArray(value)) return []
  const rows: PlantPartChild[] = []
  for (const item of value) {
    if (!item || typeof item !== 'object') continue
    const row = item as Record<string, unknown>
    const part = extractNode<PlantPartRef>(row['part'])
    const edge = extractPlantEdges([row['edge']])[0]
    if (part && edge) rows.push({ part, edge })
  }
  return rows.sort((a, b) => compareByNameThenId(a.part, b.part))
}

function vendorRole(value: unknown): PlantVendorRole | null {
  return value === 'manufacturer' || value === 'supplier' ? value : null
}

function extractVendors(value: unknown): PlantPartVendor[] {
  if (!Array.isArray(value)) return []
  const rows = new Map<string, PlantPartVendor>()
  for (const item of value) {
    if (!item || typeof item !== 'object') continue
    const row = item as Record<string, unknown>
    const vendor = extractNode<PlantVendorRef>(row['vendor'])
    const role = vendorRole(row['role'])
    if (vendor && role) rows.set(`${vendor.id}:${role}`, { vendor, role })
  }
  return [...rows.values()].sort(
    (a, b) => compareByNameThenId(a.vendor, b.vendor) || a.role.localeCompare(b.role)
  )
}

function extractProcedures(value: unknown): PlantPartProcedure[] {
  if (!Array.isArray(value)) return []
  const rows: PlantPartProcedure[] = []
  for (const item of value) {
    if (!item || typeof item !== 'object') continue
    const row = item as Record<string, unknown>
    const procedure = extractNode<PlantProcedureRef>(withDisplayName(row['procedure'], ['title']))
    const edge = extractPlantEdges([row['edge']])[0]
    if (!procedure || !edge) continue
    const requirement: PlantPartProcedure = { procedure }
    const quantity = optionalNumber(edge.props['quantity'])
    const isOptional = optionalBoolean(edge.props['is_optional'])
    if (quantity !== undefined) requirement.quantity = quantity
    if (isOptional !== undefined) requirement.isOptional = isOptional
    rows.push(requirement)
  }
  return rows.sort((a, b) => compareByNameThenId(a.procedure, b.procedure))
}

function extractLocations(value: unknown): PlantPartLocation[] {
  if (!Array.isArray(value)) return []
  const rows: PlantPartLocation[] = []
  for (const item of value) {
    if (!item || typeof item !== 'object') continue
    const row = item as Record<string, unknown>
    const location = extractNode<PlantGenericEntity>(row['location'])
    if (location) {
      rows.push({ location, locationLabels: labels(row['locationLabels']) })
    }
  }
  return rows.sort((a, b) => compareByNameThenId(a.location, b.location))
}

export function mapPlantPartDetail(raw: CypherReadResult, partId: string): PlantPartDetail {
  const row = raw.results[0] ?? {}
  const part =
    extractNode<PlantPartDetailNode>(row['p']) ??
    ({ id: partId, _id: partId, name: 'Unknown part' } satisfies PlantPartDetailNode)

  return {
    part,
    usedIn: extractUsedIn(row['usedIn']),
    subParts: extractSubParts(row['subParts']),
    vendors: extractVendors(row['vendors']),
    procedures: extractProcedures(row['procedures']),
    faults: extractFaultList(row['faults']),
    locations: extractLocations(row['locations']),
    equipment: extractNode<PlantEquipment>(row['equipment']),
    line: extractNode<PlantLine>(row['line']),
    site: extractNode<PlantSite>(row['site']),
    isStray: row['isStray'] === true
  }
}
